import { useEffect, useState } from 'react'
import { DOWNLOADS, desktop, type DesktopSettings, type UpdateState } from '@/lib/desktop'
import { AppleIcon, WindowsIcon } from '@/components/OsIcons'
import { usePresenceStore } from '@/store/presence'
import { cx } from '@/lib/cx'
import layer from './SettingsLayer.module.css'
import styles from './VoiceSection.module.css'

/** Раздел в браузере: чем полезно приложение и где его взять. */
function DownloadCard() {
  return (
    <div>
      <h1 className={layer.sectionTitle}>Приложение</h1>
      <p className={layer.text}>Тот же Кличат в своём окне. Чего нет в браузере:</p>
      <ul className={layer.points}>
        <li>выбор окна для демонстрации экрана, на Windows — со звуком приложения</li>
        <li>клавиша рации поверх игр</li>
        <li>значок в трее со счётчиком упоминаний</li>
        <li>обновления ставятся сами</li>
      </ul>
      <h2 className={layer.subTitle}>Скачать</h2>
      <div className={styles.downloads}>
        <a className={styles.downloadButton} href={DOWNLOADS.windows}><WindowsIcon /> Windows 10 и 11</a>
        <a className={styles.downloadButton} href={DOWNLOADS.mac}><AppleIcon /> macOS (Apple Silicon)</a>
        <a className={styles.downloadButton} href={DOWNLOADS.macIntel}><AppleIcon /> macOS (Intel)</a>
      </div>
      <p className={layer.text}>Приложение пока без сертификата издателя, и при первом запуске система предупреждает. Как открыть:</p>
      <ul className={layer.points}>
        <li><strong>Windows:</strong> «Подробнее» → «Выполнить в любом случае»</li>
        <li><strong>macOS:</strong> правый клик по значку → «Открыть»</li>
      </ul>
    </div>
  )
}

const TOGGLES: [keyof DesktopSettings, string, string][] = [
  ['trayOnClose', 'Сворачивать в трей при закрытии', 'Крестик прячет окно — голос и уведомления продолжают работать'],
  ['autostart', 'Запускать вместе с системой', 'Кличат стартует свёрнутым при входе в систему'],
  ['hardwareAcceleration', 'Аппаратное ускорение', 'Видео кодируется видеокартой; выключайте, если демонстрация идёт рывками. Нужен перезапуск'],
]

/** Пустое состояние: события старой оболочки приносят только версию. */
function base(s: UpdateState | null): UpdateState {
  return s ?? { status: 'idle', current: desktop?.version ?? '', latest: '', percent: 0, url: DOWNLOADS.windows, error: '', supported: true }
}

/** Человеческим языком: что сейчас происходит с обновлением. */
function describe(s: UpdateState | null): string {
  if (!s) return 'Приложение обновляется само.'
  if (!s.supported) return 'Это сборка для разработки — обновления в ней не работают.'
  switch (s.status) {
    case 'checking':
      return 'Проверяем, нет ли новой версии…'
    case 'none':
      return 'У вас последняя версия.'
    case 'downloading':
      return s.percent > 0 ? `Скачивается версия ${s.latest} — ${s.percent}%.` : `Найдена версия ${s.latest}, скачиваем…`
    case 'ready':
      return `Версия ${s.latest} скачана и встанет при перезапуске.`
    case 'manual':
      return `Вышла версия ${s.latest}. Система не дала поставить её сама — скачайте и замените приложение, вход и настройки сохранятся.`
    case 'error':
      return `Не удалось проверить обновления: ${s.error}`
    default:
      return 'Последняя проверка ещё не закончилась — обновления ищутся при запуске.'
  }
}

/** Настройки десктоп-приложения: окно и трей, автозапуск, глобальный PTT, разрешения macOS, обновления. */
export function DesktopSection() {
  const [settings, setSettings] = useState<DesktopSettings | null>(null)
  const [screenAccess, setScreenAccess] = useState<string>('granted')
  const [screenAtStart, setScreenAtStart] = useState<string | null>(null)
  const [state, setState] = useState<UpdateState | null>(null)
  const [game, setGame] = useState<{ name: string } | null>(null)
  const [checking, setChecking] = useState(false)
  // «Показывать, во что играю» — настройка учётной записи на сервере: от неё зависит и история
  // «Активность», и другой компьютер. Раньше она жила в оболочке (shareGame) — перенос в gameStatus.ts
  const showGame = usePresenceStore((s) => s.showGame)
  const setShowGame = usePresenceStore((s) => s.setShowGame)
  const [gameError, setGameError] = useState(false)
  const mac = desktop?.platform === 'darwin'

  useEffect(() => {
    if (!desktop) return
    void desktop.getSettings().then(setSettings)
    const offOverlay = desktop.overlay?.onSettings(setSettings)
    void desktop.mediaAccess('screen').then(setScreenAccess)
    void desktop.mediaAccessAtStart?.('screen').then(setScreenAtStart)
    // состояние спрашиваем сами: события приходят при запуске, а настройки открывают позже
    void desktop.updateState?.().then(setState)
    // что приложение видит прямо сейчас — иначе «не работает» не с чем сверить
    void desktop.currentGame?.().then(setGame)
    const offGame = desktop.onGame?.(setGame)
    const offState = desktop.onUpdateState?.(setState)
    const offReady = desktop.onUpdateReady((info) => setState((s) => ({ ...base(s), status: 'ready', latest: info.version })))
    const offManual = desktop.onUpdateManual?.((info) => setState((s) => ({ ...base(s), status: 'manual', latest: info.version, url: info.url })))
    return () => {
      offOverlay?.()
      offState?.()
      offGame?.()
      offReady()
      offManual?.()
    }
  }, [])

  const shell = desktop
  if (!shell) return <DownloadCard />
  const patch = (p: Partial<DesktopSettings>) => void shell.setSettings(p).then(setSettings)

  return (
    <div>
      <h1 className={layer.sectionTitle}>Приложение</h1>
      <p className={layer.text}>Версия {shell.version || '—'} · {mac ? 'macOS' : shell.platform === 'win32' ? 'Windows' : shell.platform}</p>

      <h2 className={layer.subTitle}>Обновления</h2>
      <p className={layer.text}>{describe(state)}</p>
      <p className={layer.text}>
        {mac
          ? 'Кличат проверяет обновления при запуске и каждые шесть часов. Новую версию он скачает, но поставить её нужно руками: приложение пока без сертификата Apple.'
          : 'Обновления проверяются при запуске и каждые шесть часов и ставятся сами при следующем запуске.'}
      </p>
      <div className={styles.downloads}>
        {state?.status === 'ready' ? (
          <button type="button" className={layer.button} onClick={() => shell.installUpdate()}>Перезапустить и обновить</button>
        ) : null}
        {state?.status === 'manual' ? (
          <a className={layer.button} href={state.url}>Скачать версию {state.latest}</a>
        ) : null}
        {shell.checkUpdate ? (
          <button
            type="button"
            className={cx(layer.button, layer.buttonSecondary)}
            disabled={checking || state?.status === 'checking'}
            onClick={() => {
              setChecking(true)
              void shell.checkUpdate?.().then(setState).finally(() => setChecking(false))
            }}
          >
            {checking || state?.status === 'checking' ? 'Проверяем…' : 'Проверить сейчас'}
          </button>
        ) : null}
      </div>

      <h2 className={layer.subTitle}>Окно и запуск</h2>
      <div className={styles.toggles}>
        {TOGGLES.map(([key, label, hint]) => (
          <label key={key} className={styles.toggle}>
            <input type="checkbox" checked={Boolean(settings?.[key])} disabled={!settings} onChange={(e) => patch({ [key]: e.target.checked })} />
            <span className={styles.toggleText}>
              <span className={styles.toggleLabel}>{label}</span>
              <span className={styles.toggleHint}>{hint}</span>
            </span>
          </label>
        ))}
      </div>

      <h2 className={layer.subTitle}>Игры</h2>
      <div className={styles.toggles}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={showGame}
            onChange={(e) => {
              setGameError(false)
              void setShowGame(e.target.checked).catch(() => setGameError(true))
            }}
          />
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>Показывать, во что играю</span>
            <span className={styles.toggleHint}>
              {gameError ? 'Не удалось сохранить. Попробуйте ещё раз.' : 'Друзья и участники серверов видят запущенную игру и историю игр в профиле.'}
            </span>
          </span>
        </label>
      </div>
      {shell.currentGame ? (
        <p className={layer.text}>
          {game ? `Сейчас определена игра: ${game.name}.` : 'Игра сейчас не определена.'} Игры из Steam и Epic узнаются сами,
          остальные — по списку, который приложение забирает с сервера.
        </p>
      ) : null}

      {/*
        * Разрешение выдано уже после запуска: система говорит «granted», а захват всё равно
        * не пойдёт — macOS показывает новое разрешение только новому процессу. Пока человек
        * об этом не знает, он видит невнятную ошибку и считает, что ничего не работает.
        */}
      {mac && screenAccess === 'granted' && screenAtStart !== null && screenAtStart !== 'granted' ? (
        <>
          <h2 className={layer.subTitle}>Запись экрана</h2>
          <p className={layer.text}>
            Разрешение выдано, но Кличат его ещё не видит: macOS показывает новые разрешения только
            заново запущенному приложению. Перезапустите — и демонстрация заработает.
          </p>
          {shell.relaunch ? (
            <button type="button" className={layer.button} onClick={() => void shell.relaunch?.()}>Перезапустить Кличат</button>
          ) : (
            <p className={layer.text}>Выйдите через значок в трее → «Выйти» и запустите заново: закрыть окно недостаточно.</p>
          )}
        </>
      ) : null}

      {mac && screenAccess !== 'granted' ? (
        <>
          <h2 className={layer.subTitle}>Запись экрана</h2>
          <p className={layer.text}>macOS пока не разрешил демонстрацию экрана.</p>
          <ol className={layer.steps}>
            <li>Включите Кличат в разделе «Запись экрана».</li>
            <li>Выйдите из Кличата <strong>полностью</strong>: значок в трее → «Выйти». Закрыть окно недостаточно, приложение останется в трее.</li>
            <li>Запустите Кличат заново: новое разрешение macOS показывает только новому запуску.</li>
          </ol>
          <button type="button" className={layer.button} onClick={() => shell.openPrivacySettings('screen')}>Открыть настройки</button>
        </>
      ) : null}

      {mac ? (
        <>
          <h2 className={layer.subTitle}>После обновления на маке</h2>
          <p className={layer.text}>Демонстрация экрана перестала работать после обновления, хотя галочка стоит:</p>
          <ol className={layer.steps}>
            <li>Удалите Кличат из списка «Запись экрана» кнопкой «−».</li>
            <li>Добавьте его заново кнопкой «+».</li>
            <li>Запустите Кличат заново.</li>
          </ol>
          <p className={layer.text}>Приложение пока без сертификата Apple, и macOS привязывает разрешение к конкретной сборке. С сертификатом это уйдёт.</p>
          <button type="button" className={layer.button} onClick={() => shell.openPrivacySettings('screen')}>Открыть «Запись экрана»</button>
        </>
      ) : null}
    </div>
  )
}
